import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { WINS_TICKER_AVAILABLE_IN_BUILD } from '../lib/growth-availability.ts';
import type { TickerView } from '../lib/growth-state.ts';

/**
 * CF-GROWTH-V1 — de-identified recent-wins ticker (social proof).
 *
 * Renders ONLY when the server returned a non-empty, well-formed item set (`view.kind === 'shown'`)
 * AND the build may surface it. The server already excludes the owner, de-identifies every row to
 * "Player", and returns EMPTY until there are ≥5 distinct non-owner winners, so this component
 * cannot show a single-person or owner win. It rotates through the items ~5s at a time; the copy
 * bakes "A player" because `display` is always "Player" for this ticket.
 *
 * The German build is fail-closed OUT via WINS_TICKER_AVAILABLE_IN_BUILD pending a PL/Scorer ruling
 * on whether a wins ticker is permissible on that surface (see growth-availability.ts).
 */

const GOLD = 'var(--gold)';
const ROTATE_MS = 5000;

/** Known tiers → accent colour; any other tier string falls back to the muted default. */
const TIER_DOT: Record<string, string> = {
  standard: 'rgba(255,255,255,0.5)',
  boost: '#7fd1ff',
  mega: GOLD,
};

export interface WinsTickerProps {
  view: TickerView;
}

export function WinsTicker({ view }: WinsTickerProps) {
  const t = useT();
  const items = view.kind === 'shown' ? view.items : [];
  const [idx, setIdx] = useState(0);

  // Declared unconditionally (before any early return) so hook order is stable. Rotates only when
  // there is more than one item; the modulo read below keeps `idx` valid if the set shrinks.
  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [items.length]);

  if (!WINS_TICKER_AVAILABLE_IN_BUILD) return null;
  if (items.length === 0) return null;

  const cur = items[idx % items.length];
  const dot = TIER_DOT[cur.tier] ?? TIER_DOT.standard;

  return (
    <div
      aria-live="polite"
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', borderRadius: 999,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.08)',
        fontSize: 13, color: '#cbd2dd', overflow: 'hidden', whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden="true"
        style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flex: '0 0 auto' }}
      />
      <span style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
        {t('growth.ticker.win', { amount: cur.wonTon })}
      </span>
    </div>
  );
}
